import React from "react";
import EndDateRow from "./EndDateRow";
import Typography from "Components/DesignSystem/Typography/Typography";
import { FOCUS_RING } from "Components/DesignSystem/focusRing";

/**
 * The Weekly recurrence row — pick weekdays, then an end date.
 *
 * The filename's missing "e" ("Schdule") is left alone: renaming the file is a
 * separate change with its own import churn, and the typo is not user-visible.
 *
 * **The day picker was keyboard-inaccessible.** Each day was
 * `<div role="button" onClick={...}>` with **no `tabIndex`, no `onKeyDown` and no
 * `aria-pressed`**. Announcing an element as a button without making it focusable or
 * operable by keypress is worse than leaving it a plain div — a screen-reader user is
 * told there is a control and then cannot reach it (WCAG 2.1.1, 4.1.2). Setting a
 * weekly import schedule was mouse-only. They are real `<button>`s now, with
 * `aria-pressed` carrying the selected state, so the toggle is announced rather than
 * being conveyed by a background colour alone (WCAG 1.4.1).
 *
 * **"Wednessday" is fixed.** It was rendered to the user in the "Occurs every …"
 * sentence. Only `code` reaches the payload (`buildScheduleData` reads
 * `weeksInfo[i].code`), so the spelling is display-only and safe to correct.
 */
const WeekRepeatSchdule = ({
  weeksInfo,
  scheduleInfo,
  openDatePicker,
  handleWeeklyDataChange,
  setOpenDatePicker,
  setScheduleInfo,
}) => {
  const selectedDays = weeksInfo.filter((item) => item.selected);

  return (
    <div data-testid="connectors-schedule-week">
      <Typography variant="caption-metadata" as="span" className="tw-text-ink-secondary">
        Repeat on
      </Typography>

      <div className="tw-mt-2 tw-flex tw-flex-wrap tw-gap-2" role="group" aria-label="Repeat on">
        {weeksInfo.map((item) => (
          <button
            type="button"
            key={item.code}
            onClick={() => handleWeeklyDataChange(item, item.code)}
            aria-pressed={item.selected}
            aria-label={item.day}
            data-testid={`connectors-schedule-day-${item.code}`}
            className={`tw-h-11 tw-w-11 tw-rounded-ctl tw-border tw-border-solid tw-border-accent-bold tw-text-body-primary tw-transition-colors ${FOCUS_RING} ${
              item.selected
                ? "tw-bg-accent-bold tw-text-ink-inverse"
                : "tw-bg-surface tw-text-ink hover:tw-bg-surface-hover"
            }`}
          >
            {item.displayChar}
          </button>
        ))}
      </div>

      <div className="tw-mt-3 tw-flex tw-flex-wrap tw-items-center tw-gap-1">
        <Typography variant="caption-metadata" as="span" className="tw-text-ink-secondary">
          Occurs every
        </Typography>
        {/* One node per selected day, as before — the legacy rendered a node for every
            day and left the unselected ones empty, which put six stray `&nbsp;`s in
            the sentence. */}
        {selectedDays.map((item) => (
          <Typography
            key={item.code}
            variant="caption-metadata"
            as="span"
            className="tw-text-ink"
            data-testid={`connectors-schedule-day-name-${item.code}`}
          >
            {item.day}
          </Typography>
        ))}
      </div>

      <EndDateRow
        label="Until"
        instanceId="connectors-schedule-week-end-date"
        scheduleInfo={scheduleInfo}
        setScheduleInfo={setScheduleInfo}
        openDatePicker={openDatePicker}
        setOpenDatePicker={setOpenDatePicker}
      />
    </div>
  );
};

export default WeekRepeatSchdule;
